import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiAdmin, apiTV, tokenStore, formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Brand from "@/components/Brand";

/**
 * Single sign-in screen used as the landing page.
 * The backend's POST /api/login detects whether credentials belong to an admin or
 * a TV device and we redirect accordingly. Fire Stick logins additionally enter
 * fullscreen Full-HD mode using the user's click as the required gesture.
 */
export default function Landing() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      toast.error("Enter your user and password");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiAdmin.post("/login", {
        username: identifier.trim(),
        password,
      });
      if (data.role === "admin") {
        tokenStore.setAdmin(data.token);
        await refresh();
        toast.success(`Welcome ${data.user?.name || ""}`);
        navigate("/app", { replace: true });
      } else if (data.role === "tv") {
        tokenStore.setTV(data.token);
        toast.success(`Connected · ${data.tv.location}`);
        // Trigger fullscreen Full-HD inside the user's gesture
        try {
          const el = document.documentElement;
          const req =
            el.requestFullscreen ||
            el.webkitRequestFullscreen ||
            el.mozRequestFullScreen ||
            el.msRequestFullscreen;
          if (req) await req.call(el).catch(() => {});
        } catch {
          /* not supported */
        }
        navigate("/tv", { replace: true });
      } else {
        toast.error("Unknown account type");
      }
    } catch (err) {
      // Try the TV-only endpoint as a fallback in case /login isn't available yet
      try {
        const { data } = await apiTV.post("/tv/login", {
          username: identifier.trim(),
          password,
        });
        tokenStore.setTV(data.token);
        toast.success(`Connected · ${data.tv.location}`);
        try {
          const el = document.documentElement;
          const req =
            el.requestFullscreen ||
            el.webkitRequestFullscreen ||
            el.mozRequestFullScreen ||
            el.msRequestFullscreen;
          if (req) await req.call(el).catch(() => {});
        } catch {
          /* ignore */
        }
        navigate("/tv", { replace: true });
        return;
      } catch {
        /* ignore */
      }
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[#F8FAFC]"
      data-testid="landing-page"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm cc-slide-up"
        data-testid="landing-login-form"
      >
        {/* Brand */}
        <div className="flex flex-col items-center mb-10">
          <Brand variant="dark" className="h-12 sm:h-14" />
          <div className="text-[11px] uppercase tracking-[0.25em] mt-4 text-slate-500">
            Signage Operations
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-7 bg-white ring-1 ring-slate-200 shadow-xl shadow-slate-900/[0.04]">
          <div className="space-y-4">
            <div>
              <Label htmlFor="user" className="text-xs font-medium text-slate-600">
                User
              </Label>
              <Input
                id="user"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="admin@email.com or TV username"
                autoFocus
                autoComplete="username"
                required
                className="mt-1.5 h-11"
                data-testid="landing-user-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium text-slate-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="mt-1.5 h-11"
                data-testid="landing-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="landing-submit-button"
          >
            {submitting ? "Signing in..." : "Sign in"}
            <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
          </Button>

          <div className="text-[11px] text-center mt-5 text-slate-500">
            Admins access the dashboard · TVs start playing automatically.
          </div>
        </div>

        <div className="text-center text-xs mt-6 text-slate-500">
          <a
            href="/download"
            className="inline-flex items-center gap-1.5 hover:text-slate-900 underline-offset-4 hover:underline transition-colors"
            data-testid="landing-download-link"
          >
            Descargar la app para Fire TV / Android →
          </a>
        </div>

        <div className="text-center text-xs mt-8 text-slate-400">
          © CreatiCore 2026 · Built for hospitality
        </div>
      </form>
    </div>
  );
}
